import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiPlus, FiDownload, FiGrid, FiList, FiSearch, FiUser, FiColumns, FiFileText } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import DownloadReportModal from '../../components/UI/Modal/DownloadReportModal';
import { clientesService, reportesService } from '../../services';
import { useDebounce } from '../../hooks/useDebounce';

const Clientes = () => {
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Aplicar debounce al searchQuery (espera 500ms después de que el usuario deje de escribir)
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  useEffect(() => {
    loadClientes();
  }, []);

  // Efecto para búsqueda con debounce
  useEffect(() => {
    performSearch(debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  /**
   * Transformar datos de la API al formato de la UI
   */
  const transformClienteFromAPI = useCallback((cliente) => ({
    id: cliente.id,
    nombreNegocio: cliente.company_name || cliente.full_name,
    nombreContacto: cliente.full_name || '',
    telefono: cliente.phone || '',
    email: cliente.email || '',
    categoria: cliente.client_category ? (cliente.client_category === 'DISTRIBUTOR' ? 'distributor' : 'tienda') : '',
    tipoCliente: cliente.business_type || '',
    vendedorAsignado: cliente.assigned_user_id ? cliente.assigned_user_id.toString() : '',
    fechaRegistro: cliente.created_at || '',
    ultimaCompra: cliente.last_purchase || '',
    totalCompras: cliente.total_purchases || 0,
    facturasPendientes: cliente.pending_invoices || 0,
    montoFacturasPendientes: cliente.pending_amount || 0,
    status: cliente.is_active ? 'activo' : 'inactivo'
  }), []);

  const loadClientes = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Llamada real a la API con parámetros de paginación
      const params = {
        skip: 0,
        limit: 50,
        is_active: true
      };
      
      const result = await clientesService.getClientes(params);
      
      if (result.success) {
        // Transformar los datos de la API al formato que espera la UI
        const clientesTransformados = result.data.map(transformClienteFromAPI);
        setClientes(clientesTransformados);
      } else {
        setError(result.message || 'Error al cargar clientes');
        setClientes([]);
      }
    } catch (error) {
      setError('Error de conexión al cargar clientes');
      console.error('Error de conexión al cargar clientes:', error);
      setClientes([]);
    } finally {
      setIsLoading(false);
    }
  }, [transformClienteFromAPI]);

  /**
   * Realizar búsqueda real en la API (se ejecuta con debounce)
   */
  const performSearch = useCallback(async (query) => {
    if (!query.trim()) {
      // Si no hay búsqueda, cargar todos los clientes
      loadClientes();
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      // Parámetros de búsqueda para la API
      const searchParams = {
        skip: 0,
        limit: 50,
        is_active: true,
        search: query // Parámetro de búsqueda
      };
      
      const result = await clientesService.getClientes(searchParams);
      
      if (result.success) {
        const clientesData = result.data.map(transformClienteFromAPI);
        setClientes(clientesData);
      } else {
        setError(result.message || 'Error en la búsqueda');
        setClientes([]);
      }
    } catch (err) {
      setError('Error de conexión en la búsqueda');
      console.error('Error searching clientes:', err);
      setClientes([]);
    } finally {
      setIsLoading(false);
    }
  }, [loadClientes, transformClienteFromAPI]);

  const handleAddNew = () => {
    navigate('/system/clientes/nuevo');
  };

  const handleDownload = () => {
    setShowDownloadModal(true);
  };

  const handleDownloadReport = async ({ startDate, endDate, format }) => {
    setIsDownloading(true);

    try {
      // Llamar al servicio de reportes
      const params = {
        format: format, // csv o pdf
        date_from: startDate,
        date_to: endDate,
        is_active: true // Solo clientes activos
      };

      const result = await reportesService.exportarClientes(params);

      if (result.success) {
        // Determinar el tipo MIME según el formato
        const mimeTypes = {
          csv: 'text/csv',
          pdf: 'application/pdf',
          excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        };

        // Determinar la extensión del archivo
        const extensions = {
          csv: 'csv',
          pdf: 'pdf',
          excel: 'xlsx'
        };

        // Crear blob y descargar
        const blob = new Blob([result.data], {
          type: mimeTypes[format] || 'application/octet-stream'
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        // Generar nombre de archivo apropiado
        const fileName = startDate && endDate
          ? `clientes_${startDate}_${endDate}.${extensions[format]}`
          : `clientes_todos_${new Date().toISOString().split('T')[0]}.${extensions[format]}`;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Cerrar modal y mostrar mensaje de éxito
        setShowDownloadModal(false);
        alert(`Reporte descargado exitosamente en formato ${format.toUpperCase()}`);
      } else {
        throw new Error(result.message || 'Error al generar el reporte');
      }
    } catch (error) {
      console.error('Error al descargar reporte:', error);
      alert('Error al descargar el reporte. Por favor intente nuevamente.');
    } finally {
      setIsDownloading(false);
    }
  };

  // Manejar búsqueda (actualiza query, la búsqueda real se hace con debounce)
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Limpiar búsqueda
  const handleClearSearch = () => {
    setSearchQuery('');
  };

  // Limpiar errores
  const handleClearError = () => {
    setError(null);
    loadClientes();
  };

  const getThumbnailType = (categoria) => {
    // Todos los clientes usan el mismo color morado corporativo
    return 'blue-dark-gradient';
  };







  if (isLoading) {
    return <LoadingGrid message="Cargando clientes..." />;
  }

  return (
    <div className="clientes-page">
      {/* Error message */}
      {error && (
        <div className="alert alert--danger">
          <FiFileText className="alert-icon" />
          <div className="alert-content">
            <strong>Error:</strong> {error}
            <button 
              className="btn btn--ghost btn--sm" 
              onClick={handleClearError}
              style={{ marginLeft: '10px' }}
            >
              Reintentar
            </button>
          </div>
        </div>
      )}
      
      {/* Título */}
      <h1 className="page-title">CLIENTES</h1>

      {/* Menú de acciones */}
      <div className="page-header-with-menu">
        <div className="action-menu">
          <button className="menu-item menu-item--first" onClick={handleAddNew}>
            + AGREGAR CLIENTE
          </button>
          
          <div className="menu-item menu-item--search">
            <label className="search-label">BUSCAR:</label>
            <input
              type="text"
              className="search-input"
              placeholder="NOMBRE DEL CLIENTE"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
          
          <button className="menu-item menu-item--download" onClick={handleDownload}>
            DESCARGAR LISTA ↓
          </button>
          
          <div className="menu-item menu-item--views">
            <button
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Vista de tarjetas"
            >
              <FiGrid />
            </button>
            <button
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="Vista de lista"
            >
              <FiList />
            </button>
            <button
              className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Vista de tabla"
            >
              <FiColumns />
            </button>
          </div>
        </div>
      </div>

      {/* Contenido */}
      {clientes.length === 0 && !isLoading && !error ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <FiFileText size={48} />
          </div>
          <h3 className="empty-state-title">
            {searchQuery ? 'No se encontraron clientes' : 'No hay clientes registrados'}
          </h3>
          <p className="empty-state-description">
            {searchQuery 
              ? `No se encontraron clientes que coincidan con "${searchQuery}"`
              : 'Comienza agregando tu primer cliente al sistema'
            }
          </p>
          {searchQuery && (
            <p>
              <button 
                className="btn btn--ghost" 
                onClick={handleClearSearch}
              >
                Limpiar búsqueda
              </button>
            </p>
          )}
        </div>
      ) : viewMode === 'table' ? (
        <div className="table-container">
          <table className="data-table data-table--clientes">
            <thead>
              <tr>
                <th>NOMBRE DEL NEGOCIO</th>
                <th>CONTACTO</th>
                <th>TELÉFONO</th>
                <th>EMAIL</th>
                <th className="text-center">CATEGORÍA</th>
                <th className="text-center">TIPO</th>
                <th className="text-center">FACT. PENDIENTES</th>
                <th className="text-center">ESTADO</th>
              </tr>
            </thead>
            <tbody>
              {clientes.map((cliente) => (
                <tr
                  key={cliente.id}
                  onClick={() => navigate(`/system/clientes/${cliente.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <td><strong>{cliente.nombreNegocio}</strong></td>
                  <td>{cliente.nombreContacto}</td>
                  <td>{cliente.telefono || '-'}</td>
                  <td>{cliente.email || '-'}</td>
                  <td className="text-center">
                    <span className={`badge badge-${cliente.categoria}`}>
                      {cliente.categoria === 'distributor' ? 'Distribuidor' : 'Tienda'}
                    </span>
                  </td>
                  <td className="text-center">{cliente.tipoCliente || '-'}</td>
                  <td className="text-center">
                    {cliente.facturasPendientes > 0 ? (
                      <span className="badge badge-warning">
                        {cliente.facturasPendientes}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="text-center">
                    <span className={`badge badge-${cliente.status}`}>
                      {cliente.status === 'activo' ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={viewMode === 'grid' ? 'content-grid' : 'content-list'}>
          {clientes.map((cliente) => (
            <div
              key={cliente.id}
              className={viewMode === 'grid' ? 'content-card' : 'content-card-list'}
              onClick={() => navigate(`/system/clientes/${cliente.id}`)}
              style={{ cursor: 'pointer' }}
            >
              {viewMode === 'grid' ? (
                <>
                  <div className={`content-thumbnail ${getThumbnailType(cliente.categoria)}`}>
                    <FiUser className="thumbnail-icon" />
                  </div>
                  <div className="content-info">
                    <div className="content-title">{cliente.nombreNegocio}</div>
                    <div className="content-subtitle">{cliente.nombreContacto}</div>
                  </div>
                </>
              ) : (
                <>
                  <div className={`content-thumbnail-small ${getThumbnailType(cliente.categoria)}`}>
                    <FiUser className="thumbnail-icon-small" />
                  </div>
                  <div className="content-info-list">
                    <div className="content-title">{cliente.nombreNegocio}</div>
                    <div className="content-subtitle">{cliente.nombreContacto}</div>
                    <div className="content-meta">
                      <span className="meta-item">Tel: {cliente.telefono}</span>
                      <span className="meta-item">{cliente.categoria}</span>
                      {cliente.facturasPendientes > 0 && (
                        <span className="meta-item warning">Facturas pendientes: {cliente.facturasPendientes}</span>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal de descarga de reportes */}
      <DownloadReportModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        onDownload={handleDownloadReport}
        title="Descargar Reporte de Clientes"
        moduleName="clientes"
        isLoading={isDownloading}
      />
    </div>
  );
};

export default Clientes;